// @ts-nocheck
import React, { useState, useRef, useEffect, FC } from 'react'
import styled, { keyframes } from 'styled-components'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import useLogs from '../../../hooks/useLogs'
import { SupportedChainIdValue } from '../../../constants/networks'
import Select from 'react-select'
import LogsItem from './LogItem'
import { shortenAddress } from '../../../utils/web3-utils'
import useEnviromenmentList from '../../../hooks/useEnviromenmentList'

interface LogsProps {
  chainId: number
}

interface Filter {
  token?: string
  status?: string
  executionPlanId?: string
  chainId?: SupportedChainIdValue | number
}

interface PaginationControlsProps {
  currentPage: number
  totalPages?: number
  onPageChange: (newPage: number) => void
}

const defaultStatus = [
  'notSimulated',
  'simulationFailed',
  'simulationReverted',
  'simulationSucceeded',
  'executionDelayed',
  'executionSucceeded',
  'executionReverted',
  'executionNotReached',
  'transactionReverted',
]

const statusList = [
  { value: 'notSimulated', label: 'notSimulated' },
  { value: 'simulationFailed', label: 'simulationFailed' },
  { value: 'simulationReverted', label: 'simulationReverted' },
  { value: 'simulationSucceeded', label: 'simulationSucceeded' },
  { value: 'executionDelayed', label: 'executionDelayed' },
  { value: 'executionSucceeded', label: 'executionSucceeded' },
  { value: 'executionReverted', label: 'executionReverted' },
  { value: 'executionNotReached', label: 'executionNotReached' },
  { value: 'transactionReverted', label: 'transactionReverted' },
]

const Logs: FC<LogsProps> = ({ chainId }) => {
  const params = useParams<{ id: string }>()
  const [selectedStatus, setSelectedStatus] = useState<string[]>(defaultStatus)
  const [selectedToken, setSelectedToken] = useState<string>('')
  const [selectedColored, setSelectedColored] = useState<boolean>(true)
  const [openTab, setOpenTab] = useState<string>('')
  const [selectedPlanId, setSelectedPlanId] = useState<string>('')
  const [intervalMs, setIntervalMs] = useState<boolean>(false)
  const [filters, setFilters] = useState<Filter>({})
  const [page, setPage] = useState<number>(1)
  const { data, isLoading, isRefetching } = useLogs(params.id, page, 70, filters, intervalMs ? 5000 : 0)
  const { data: envData } = useEnviromenmentList(params.id)
  const navigate = useNavigate()
  const location = useLocation()

  const updateURL = (newFilters) => {
    const searchParams = new URLSearchParams()
    Object.keys(newFilters).forEach((key) => {
      const value = newFilters[key]
      if (value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(`${key}[]`, String(v)))
        } else {
          searchParams.set(key, String(value))
        }
      } else {
        searchParams.delete(key)
      }
    })
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true })
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const token = searchParams.get('token') || ''
    const statusValues = searchParams.getAll('status[]').length > 0 ? searchParams.getAll('status[]') : defaultStatus
    const realtimeString = searchParams.get('realtime')
    const coloredString = searchParams.get('colored') || true
    const executionPlanId = searchParams.get('executionPlanId') || ''
    setSelectedToken(token)
    setSelectedStatus(statusValues)
    setFilters({ token, status: statusValues, executionPlanId, chainId })
    setSelectedColored(coloredString)
    setIntervalMs(realtimeString === 'true')
    setSelectedPlanId(executionPlanId)
    updateURL({ token, status: statusValues, executionPlanId, chainId })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId])

  const handlePageChange = (newPage: number) => setPage(newPage)

  const handleRealTimeChange = () => {
    setIntervalMs(!intervalMs)
    updateURL({ ...filters, colored: selectedColored, realtime: !intervalMs })
  }

  const handleColored = () => {
    setSelectedColored(!selectedColored)
    updateURL({ ...filters, realtime: intervalMs, colored: !selectedColored })
  }

  const handleSelectMultiStatus = (e) => {
    const statuses = e?.map((s) => s.value)
    setSelectedStatus(statuses)
    setPage(1)
    if (e) {
      setFilters({ ...filters, status: statuses })
      updateURL({ ...filters, status: statuses })
    } else {
      const { status, ...otherFilters } = filters
      setFilters({ ...otherFilters })
      updateURL({ ...otherFilters })
    }
  }

  const handleSelectToken = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTokenValue = e.target.value
    setSelectedToken(newTokenValue)
    setPage(1)
    if (newTokenValue !== '') {
      setFilters({ ...filters, token: newTokenValue })
      updateURL({ ...filters, token: newTokenValue })
    } else {
      const { token, ...otherFilters } = filters
      setFilters({ ...otherFilters })
      updateURL({ ...otherFilters })
    }
  }

  const handleSelectPlanId = (newPlanId: string) => {
    setSelectedPlanId(newPlanId)
    setSelectedStatus([])
    setPage(1)
    setTimeout(() => {
      if (newPlanId !== '') {
        setFilters({ ...filters, executionPlanId: newPlanId })
        updateURL({ ...filters, executionPlanId: newPlanId })
      } else {
        const { executionPlanId, ...otherFilters } = filters
        setFilters({ ...otherFilters })
        updateURL({ ...otherFilters })
      }
    }, 500)
  }

  const handleReset = () => {
    setSelectedToken('')
    setSelectedPlanId('')
    setSelectedStatus(defaultStatus)
    setOpenTab('')
    setPage(1)
    const { executionPlanId, ...otherFilters } = filters
    setFilters({ ...otherFilters })
    updateURL({ ...otherFilters })
  }

  const handleTab = (tab: string) => {
    setOpenTab(openTab === tab ? '' : tab)
  }

  const showOptions = (list) => {
    if (!list) return []
    return list.map((o) => ({ value: o, label: o }))
  }

  return (
    <Wrap>
      <FilterBar>
        <FilterBtn active={openTab === 'taskFilter'} onClick={() => handleTab('taskFilter')}>
          Status
          {selectedStatus?.length > 0 && selectedStatus?.length < defaultStatus.length && (
            <Badge>{selectedStatus.length}</Badge>
          )}
        </FilterBtn>

        <FilterBtn active={openTab === 'tokenFilter'} onClick={() => handleTab('tokenFilter')}>
          Token
          {selectedToken && <ValueBadge>{shortenAddress(selectedToken)}</ValueBadge>}
        </FilterBtn>

        <FilterBtn active={openTab === 'planFilter'} onClick={() => handleTab('planFilter')}>
          Plan ID
          {selectedPlanId && <ValueBadge>{shortenAddress(selectedPlanId)}</ValueBadge>}
        </FilterBtn>

        <FilterBtn onClick={handleReset}>Reset</FilterBtn>

        <Divider />

        <ToggleBtn active={selectedColored} onClick={handleColored}>
          Colored <TogglePip active={selectedColored} />
        </ToggleBtn>

        <ToggleBtn active={intervalMs} onClick={handleRealTimeChange}>
          Real Time <TogglePip active={intervalMs} />
        </ToggleBtn>

        {(isLoading || isRefetching) && <LoaderDot />}
      </FilterBar>

      {openTab && (
        <FilterDropdown>
          {openTab === 'taskFilter' && (
            <StyledSelect
              classNamePrefix="Select"
              isMulti
              value={showOptions(selectedStatus)}
              onChange={handleSelectMultiStatus}
              options={statusList}
              placeholder="Filter by status..."
            />
          )}
          {openTab === 'tokenFilter' && (
            <FilterInput
              value={selectedToken}
              placeholder="Token address"
              onChange={handleSelectToken}
            />
          )}
          {openTab === 'planFilter' && (
            <FilterInput
              value={selectedPlanId}
              placeholder="Execution plan ID"
              onChange={(e) => handleSelectPlanId(e.target.value)}
            />
          )}
        </FilterDropdown>
      )}

      {isLoading ? (
        <LoaderBar />
      ) : data ? (
        <>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>Network</Th>
                  <Th>Task</Th>
                  <Th>Status</Th>
                  <Th>Details</Th>
                  <Th>Plan ID</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody>
                {data?.data?.map((task, i) => (
                  <LogsItem
                    key={i}
                    item={task}
                    index={i + 1}
                    width={1200}
                    colored={selectedColored}
                    handleSelectPlanId={handleSelectPlanId}
                  />
                ))}
              </tbody>
            </Table>
          </TableWrap>
          <PaginationControls currentPage={page} totalPages={data?.pages} onPageChange={handlePageChange} />
        </>
      ) : (
        <EmptyState>No logs found</EmptyState>
      )}
    </Wrap>
  )
}

const PaginationControls: React.FC<PaginationControlsProps> = ({ currentPage, totalPages, onPageChange }) => (
  <Pagination>
    <PageBtn onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
      ← Prev
    </PageBtn>
    <PageInfo>
      {currentPage} / {totalPages ?? '—'}
    </PageInfo>
    <PageBtn onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
      Next →
    </PageBtn>
  </Pagination>
)

export default Logs

// ─── Styles ─────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`

const FilterBtn = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  background: ${({ active }) => active ? 'rgba(111, 92, 230, 0.15)' : 'rgba(255, 255, 255, 0.04)'};
  border: 1px solid ${({ active }) => active ? 'rgba(111, 92, 230, 0.4)' : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 6px;
  color: ${({ active }) => active ? '#a89aff' : '#a0a0a0'};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;

  &:hover {
    border-color: rgba(255, 255, 255, 0.15);
    color: #ededed;
  }
`

const ToggleBtn = styled(FilterBtn)`
  gap: 8px;
`

const TogglePip = styled.span<{ active?: boolean }>`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ active }) => active ? '#33C2B0' : '#3a3a3a'};
  flex-shrink: 0;
`

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  background: rgba(111, 92, 230, 0.2);
  border-radius: 9px;
  font-size: 10px;
  color: #a89aff;
`

const ValueBadge = styled.span`
  padding: 1px 6px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 4px;
  font-size: 11px;
  font-family: 'SF Mono', 'Fira Code', monospace;
  color: #ededed;
`

const Divider = styled.div`
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.08);
  margin: 0 2px;
`

const FilterDropdown = styled.div`
  background: #161616;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 12px;
`

const FilterInput = styled.input`
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #ededed;
  font-size: 13px;
  font-family: 'SF Mono', 'Fira Code', monospace;
  width: 320px;
  padding: 0 12px;
  height: 34px;
  outline: none;
  transition: border-color 0.15s;

  &::placeholder { color: #a1a1a1; }
  &:focus { border-color: rgba(111, 92, 230, 0.5); }
`

const StyledSelect = styled(Select)`
  min-width: 320px;

  .Select__control {
    background: #111;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    box-shadow: none;
    min-height: 34px;
    &:hover { border-color: rgba(255, 255, 255, 0.15); }
  }
  .Select__control--is-focused {
    border-color: rgba(111, 92, 230, 0.5) !important;
  }
  .Select__multi-value {
    background: rgba(111, 92, 230, 0.15);
    border-radius: 4px;
  }
  .Select__multi-value__label { color: #a89aff; font-size: 11px; }
  .Select__multi-value__remove {
    color: #a89aff;
    &:hover { background: rgba(111, 92, 230, 0.3); color: #fff; }
  }
  .Select__placeholder { color: #a1a1a1; font-size: 13px; }
  .Select__input-container { color: #ededed; }
  .Select__indicator-separator { display: none; }
  .Select__dropdown-indicator { color: #a1a1a1; }
  .Select__clear-indicator { color: #a1a1a1; &:hover { color: #a0a0a0; } }
  .Select__menu {
    background: #161616;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
    margin-top: 4px;
  }
  .Select__menu-list { padding: 4px; }
  .Select__option {
    background: transparent;
    color: #a0a0a0;
    font-size: 12px;
    border-radius: 4px;
    &.Select__option--is-focused { background: rgba(255, 255, 255, 0.05); color: #ededed; }
    &.Select__option--is-selected { background: rgba(111, 92, 230, 0.15); color: #a89aff; }
  }
`

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

const LoaderBar = styled.div`
  height: 2px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 2px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    display: block;
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(111, 92, 230, 0.5), transparent);
    animation: ${shimmer} 1.4s ease infinite;
  }
`

const spin = keyframes`
  to { transform: rotate(360deg); }
`

const LoaderDot = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(111, 92, 230, 0.2);
  border-top-color: #6f5ce6;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`

const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 8px;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

const Th = styled.th`
  padding: 10px 12px;
  text-align: left;
  color: #a1a1a1;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`

const EmptyState = styled.div`
  color: #777;
  font-size: 13px;
  padding: 40px 0;
  text-align: center;
`

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-top: 4px;
`

const PageBtn = styled.button`
  height: 30px;
  padding: 0 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  color: #a0a0a0;
  font-size: 12px;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    border-color: rgba(255, 255, 255, 0.15);
    color: #ededed;
  }

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
`

const PageInfo = styled.span`
  color: #777;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
`

export { Details } from './LogItem'
